import React, { useEffect, useMemo } from 'react';

import TextInput from 'components/forms/TextInput/TextInput';
import RoundedButton from 'components/RoundedButton/RoundedButton';
import SelectField from 'components/SelectField';
import CardsSkeleton from 'components/skeletons/CardsSkeleton';
import { periodUnitOptions, PACKAGE_NAMES } from 'Customers/constants';

const periodMultipliers = {
  MONTH: 1,
  QUARTERLY: 3,
  SEMI_ANNUAL: 6,
  YEAR: 12,
};

const isFlatFeePlan = (plan) => plan?.pricingModel === 'FLAT_FEE';

const calculateFlatFeeForPeriod = (plan, periodUnit) => {
  const defaultUnitPrice = Number(plan?.defaultUnitPrice || 0);
  const storedPeriod = plan?.defaultBillingPeriod || 'MONTH';
  const requestedPeriod = periodUnit || storedPeriod;
  const monthlyPrice = defaultUnitPrice / (periodMultipliers[storedPeriod] || 1);
  const periodPrice = monthlyPrice * (periodMultipliers[requestedPeriod] || 1);

  return Number(periodPrice.toFixed(2));
};

const getPlanPrice = (plan, periodUnit) =>
  isFlatFeePlan(plan) ? calculateFlatFeeForPeriod(plan, periodUnit) : plan?.defaultUnitPrice;

const CustomerSubscriptionDetailsInputs = ({ formik, isLoading, data = [] }) => {
  const primaryPlans = useMemo(
    () => data.filter((item) => item?.isPrimary && item?.pricingModel !== 'ONE_TIME'),
    [data]
  );
  const selectedPlan = primaryPlans.find((item) => item.identifier === formik.values.plan) || primaryPlans[0];
  const packageOptions = primaryPlans.map((item) => ({
    id: item.identifier,
    name: PACKAGE_NAMES[item.name] || item.platformName || item.name || item.identifier,
  }));

  useEffect(() => {
    if (!selectedPlan || formik.values.plan === selectedPlan.identifier) {
      return;
    }

    formik.setFieldValue('plan', selectedPlan.identifier);
    formik.setFieldValue('pricingModel', selectedPlan.pricingModel);
    formik.setFieldValue('pricingUnit', selectedPlan.pricingUnit);
    formik.setFieldValue('defaultBillingPeriod', selectedPlan.defaultBillingPeriod);
    formik.setFieldValue('unitPrice', getPlanPrice(selectedPlan, formik.values.periodUnit));
    if (isFlatFeePlan(selectedPlan)) {
      formik.setFieldValue('numberOfUsers', 1);
    }
    formik.setFieldValue('unitPriceAfter', null);
    formik.setFieldValue('unitPriceBeforeDiscount', null);
    formik.setFieldValue('discountAmount', 0);
  }, [
    formik.setFieldValue,
    formik.values.periodUnit,
    formik.values.plan,
    selectedPlan?.defaultBillingPeriod,
    selectedPlan?.defaultUnitPrice,
    selectedPlan?.identifier,
    selectedPlan?.pricingModel,
    selectedPlan?.pricingUnit,
  ]);

  const handlePlanChange = (name) => (value) => {
    const nextPlan = primaryPlans.find((item) => item.identifier === value);

    formik.setFieldValue(name, value);
    if (!nextPlan) {
      return;
    }

    formik.setFieldValue('pricingModel', nextPlan.pricingModel);
    formik.setFieldValue('pricingUnit', nextPlan.pricingUnit);
    formik.setFieldValue('defaultBillingPeriod', nextPlan.defaultBillingPeriod);
    formik.setFieldValue('unitPrice', getPlanPrice(nextPlan, formik.values.periodUnit));
    formik.setFieldValue(
      'numberOfUsers',
      isFlatFeePlan(nextPlan) ? 1 : formik.values.numberOfUsers === 1 ? '' : formik.values.numberOfUsers
    );
    formik.setFieldValue('unitPriceAfter', null);
    formik.setFieldValue('unitPriceBeforeDiscount', null);
    formik.setFieldValue('discountAmount', 0);
  };

  const handlePeriodChange = (name) => (value) => {
    formik.setFieldValue(name, value);
    if (!isFlatFeePlan(selectedPlan)) {
      return;
    }

    formik.setFieldValue('unitPrice', getPlanPrice(selectedPlan, value));
    formik.setFieldValue('unitPriceAfter', null);
    formik.setFieldValue('unitPriceBeforeDiscount', null);
    formik.setFieldValue('discountAmount', 0);
  };

  const handleUnitPriceChange = (event) => {
    const unitPrice = parseFloat(event.target.value);

    formik.setValues((values) => ({
      ...values,
      unitPrice: Number.isNaN(unitPrice) ? '' : unitPrice,
      unitPriceAfter: null,
      unitPriceBeforeDiscount: null,
      discountAmount: 0,
    }));
  };

  const handleDiscount = () => {
    const currentUnitPrice = Number(formik.values.unitPrice);
    const percentage = Number(formik.values.discount);

    if (!currentUnitPrice || !percentage) {
      return;
    }

    const normalizedPercentage = Math.min(Math.max(percentage, 0), 100);
    const discountDecimal = normalizedPercentage / 100;
    const discountPrice = Number((currentUnitPrice * (1 - discountDecimal)).toFixed(2));
    const discountAmount = Number((currentUnitPrice - discountPrice).toFixed(2));

    formik.setFieldValue('unitPriceBeforeDiscount', currentUnitPrice);
    formik.setFieldValue('unitPrice', discountPrice);
    formik.setFieldValue('unitPriceAfter', discountPrice);
    formik.setFieldValue('discountAmount', discountAmount);
  };
  return (
    <>
      {isLoading && <CardsSkeleton col={12} height={289} num={1} />}
      {!isLoading && (
        <div className='row'>
          <div className='col-md-6 mb-3'>
            <SelectField
              isCustom
              name='plan'
              label='package'
              placeholder='Select'
              options={packageOptions}
              value={formik.values.plan ?? ''}
              onChange={handlePlanChange}
              onBlur={formik.onBlur}
              error={formik.touched.plan && formik.errors.plan}
              isRequired
            />
          </div>
          <div className='col-md-6 mb-3'>
            <SelectField
              isCustom
              label='Period'
              name='periodUnit'
              placeholder='Select'
              options={periodUnitOptions}
              value={formik.values.periodUnit ?? ''}
              onChange={handlePeriodChange}
              onBlur={formik.onBlur}
              error={formik.touched.periodUnit && formik.errors.periodUnit}
              useTranslate
              isRequired
            />
          </div>
          {!isFlatFeePlan(selectedPlan) && (
            <div className='col-md-6 mb-3'>
              <TextInput
                name='numberOfUsers'
                label='number of users'
                type='number'
                value={formik.values.numberOfUsers}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isRequired
                error={formik.touched.numberOfUsers && formik.errors.numberOfUsers}
              />
            </div>
          )}
          <div className='col-md-6 mb-3'>
            <TextInput
              name='unitPrice'
              label={isFlatFeePlan(selectedPlan) ? 'Flat fee' : 'unit Price'}
              type='number'
              value={formik.values.unitPrice}
              onChange={handleUnitPriceChange}
              onBlur={formik.handleBlur}
              isRequired
              suffix={selectedPlan?.currency}
              error={formik.touched.unitPrice && formik.errors.unitPrice}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <div className='d-flex justify-content-start align-items-center gap-4'>
              <TextInput
                name='discount'
                label='discount'
                type='number'
                value={formik.values.discount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.discount && formik.errors.discount}
                isDisabled={formik.values.unitPrice === formik.values.unitPriceAfter}
                suffix={'%'}
              />
              {formik.values.unitPrice === formik.values.unitPriceAfter ? (
                <p className='text-primary ms-5 d-flex align-items-center'>
                  <i className='icon-check-mark me-1' />
                  <span>Applied</span>
                </p>
              ) : (
                <RoundedButton text={'Apply'} btnStyle='contained' color='primary' size='sm' onClick={handleDiscount} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerSubscriptionDetailsInputs;
